import React, { useEffect, useState } from "react";
import { http, formatError, pruneEmpty } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileText, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

export default function OwnerContracts() {
  const [contracts, setContracts] = useState([]);
  const [communities, setCommunities] = useState([]);

  async function load() {
    const [c, com] = await Promise.all([http.get("/ops/contracts"), http.get("/communities")]);
    setContracts(c.data);
    setCommunities(com.data);
  }
  useEffect(() => { load(); }, []);

  async function updateStatus(id, status) {
    try { await http.patch(`/ops/contracts/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  const totalMRR = contracts.filter((c) => ["signed", "active"].includes(c.status)).reduce((s, c) => s + (c.mrr_cents || 0), 0);
  const draftCount = contracts.filter((c) => c.status === "draft").length;
  const sentCount = contracts.filter((c) => c.status === "sent").length;

  return (
    <div className="space-y-6" data-testid="owner-contracts">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Contracts</div>
          <h1 className="font-display text-3xl font-bold text-primary">Contract Management</h1>
        </div>
        <NewContractDialog communities={communities} onCreated={load} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Active MRR" value={`$${(totalMRR / 100).toLocaleString()}`} accent="secondary" />
        <Stat label="Draft" value={draftCount} />
        <Stat label="Sent — awaiting signature" value={sentCount} />
      </div>

      <Card className="border-border/60"><CardContent className="p-0">
        {contracts.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No contracts yet. Create your first B2B contract to start tracking commitments.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Community</th>
                <th className="text-left p-3">Term</th>
                <th className="text-right p-3">MRR</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className="border-t border-border/60">
                  <td className="p-3 font-medium">{c.title}</td>
                  <td className="p-3 text-muted-foreground">{c.community?.name || "—"}</td>
                  <td className="p-3 text-muted-foreground text-xs">{c.start_date} → {c.end_date || "—"}</td>
                  <td className="p-3 text-right font-semibold">${(c.mrr_cents / 100).toLocaleString()}</td>
                  <td className="p-3">
                    <Badge className={
                      c.status === "signed" || c.status === "active" ? "bg-secondary/10 text-secondary border border-secondary/20" :
                      c.status === "sent" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                      "bg-muted text-muted-foreground border border-border"
                    }>
                      {c.status === "signed" || c.status === "active" ? <CheckCircle2 size={10} className="inline mr-1" /> :
                       c.status === "sent" ? <Clock size={10} className="inline mr-1" /> : null}
                      {c.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right space-x-1">
                    {c.status === "draft" && <Button size="sm" variant="outline" onClick={() => updateStatus(c.id, "sent")}>Send</Button>}
                    {c.status === "sent" && <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => updateStatus(c.id, "signed")}>Mark signed</Button>}
                    {(c.status === "signed" || c.status === "sent") && <Button size="sm" variant="ghost" onClick={() => updateStatus(c.id, "cancelled")}>Cancel</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === "secondary" ? "text-secondary" : "text-primary";
  return <Card className="border-border/60"><CardContent className="p-5">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-display text-3xl font-bold mt-1 ${cls}`}>{value}</div>
  </CardContent></Card>;
}

function NewContractDialog({ communities, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", community_id: "", contract_type: "b2b_community", start_date: "", end_date: "", mrr_cents: 0, terms: "" });
  async function submit() {
    try {
      const payload = pruneEmpty({ ...form, mrr_cents: Number(form.mrr_cents) || 0 });
      await http.post("/ops/contracts", payload);
      toast.success("Contract created"); setOpen(false); onCreated();
      setForm({ title: "", community_id: "", contract_type: "b2b_community", start_date: "", end_date: "", mrr_cents: 0, terms: "" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="new-contract-btn"><Plus size={14} className="mr-1" /> New contract</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New contract</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-contract-title" /></div>
          <div><Label className="text-xs">Community</Label>
            <Select value={form.community_id} onValueChange={(v) => setForm({ ...form, community_id: v })}>
              <SelectTrigger><SelectValue placeholder="Choose a community" /></SelectTrigger>
              <SelectContent>{communities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label className="text-xs">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">MRR (cents)</Label><Input type="number" value={form.mrr_cents} onChange={(e) => setForm({ ...form, mrr_cents: e.target.value })} data-testid="new-contract-mrr" /></div>
          <div><Label className="text-xs">Terms</Label><Textarea rows={3} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></div>
          <Button onClick={submit} disabled={!form.title || !form.start_date} className="w-full bg-primary hover:bg-primary/90" data-testid="new-contract-save">Create draft</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
